import api from './api';
import {
  GustoUser,
  GustoSyncStatus,
  GustoSyncResult,
  GustoMemberData,
  GustoContractorPaymentsPage,
  Member,
} from '../types';

export interface GustoAuthStatus {
  connected: boolean;
  serviceConfigured: boolean;
  expiresAt?: string;
  scope?: string;
  connectedAt?: string;
  lastRefreshedAt?: string;
  connectedByUserEmail?: string;
}

interface AuthorizeUrlResponse {
  authorizationUrl: string;
}

/**
 * Gusto Service
 * Centralizes all Gusto auth, sync, and user assignment API calls
 */
class GustoService {
  private baseUrl = '/gusto';

  /**
   * Get the Gusto OAuth connection status for the current organization
   */
  async getStatus(): Promise<GustoAuthStatus> {
    const response = await api.get<GustoAuthStatus>(`${this.baseUrl}/auth/status`);
    return response.data;
  }

  /**
   * Get the Gusto OAuth authorization URL
   */
  async getAuthorizeUrl(): Promise<string> {
    const response = await api.get<AuthorizeUrlResponse>(`${this.baseUrl}/auth/authorize`);
    return response.data.authorizationUrl;
  }

  /**
   * Disconnect the Gusto integration
   */
  async disconnect(): Promise<void> {
    await api.delete(`${this.baseUrl}/auth/disconnect`);
  }

  /**
   * List all Gusto users not yet linked to any member
   */
  async listUnassignedGustoUsers(): Promise<GustoUser[]> {
    const response = await api.get<GustoUser[]>(`${this.baseUrl}/sync/gusto-users/unassigned`);
    return response.data;
  }

  /**
   * Assign a Gusto user to a member
   */
  async assignGustoUser(gustoUserId: string, memberId: string): Promise<Member> {
    const response = await api.post<Member>(
      `${this.baseUrl}/sync/gusto-users/${gustoUserId}/assign`,
      null,
      { params: { memberId } }
    );
    return response.data;
  }

  /**
   * Unassign a Gusto user from a member
   */
  async unassignGustoUser(gustoUserId: string): Promise<Member> {
    const response = await api.post<Member>(
      `${this.baseUrl}/sync/gusto-users/${gustoUserId}/unassign`
    );
    return response.data;
  }

  /**
   * Get the current user's own Gusto data
   */
  async getMyGustoData(): Promise<GustoMemberData> {
    const response = await api.get<GustoMemberData>(`${this.baseUrl}/me`);
    return response.data;
  }

  /**
   * Get the Gusto data for a specific member. Requires MANAGER+ access to the
   * member (reporting chain) or BILLING/ADMIN, enforced by the backend.
   */
  async getMemberGustoData(memberId: string): Promise<GustoMemberData> {
    const response = await api.get<GustoMemberData>(`${this.baseUrl}/members/${memberId}`);
    return response.data;
  }

  /**
   * Get a page of contractor payment history for a Gusto contractor. Requires
   * MANAGER+ access to the contractor (reporting chain) or BILLING/ADMIN,
   * enforced by the backend. Only applies to CONTRACTOR-type Gusto users.
   * `page` is 1-based; `perPage` defaults to the backend's page size.
   */
  async getContractorPayments(
    contractorUuid: string,
    page: number = 1,
    perPage: number = 25
  ): Promise<GustoContractorPaymentsPage> {
    const response = await api.get<GustoContractorPaymentsPage>(
      `${this.baseUrl}/contractors/${contractorUuid}/payments`,
      { params: { page, perPage } }
    );
    return response.data;
  }

  /**
   * Get the latest Gusto sync status for the current organization
   */
  async getSyncStatus(): Promise<GustoSyncStatus> {
    const response = await api.get<GustoSyncStatus>(`${this.baseUrl}/sync/status`);
    return response.data;
  }

  /**
   * Sync employees from Gusto
   */
  async syncEmployees(): Promise<GustoSyncResult> {
    const response = await api.post<GustoSyncResult>(`${this.baseUrl}/sync/employees`);
    return response.data;
  }

  /**
   * Sync contractors from Gusto
   */
  async syncContractors(): Promise<GustoSyncResult> {
    const response = await api.post<GustoSyncResult>(`${this.baseUrl}/sync/contractors`);
    return response.data;
  }
}

// Export singleton instance
export const gustoService = new GustoService();
